import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

interface FormBlock {
  isDirty: boolean;
  isSubmitting?: boolean;
  message: string;
}

// This custom hook is consumed for forms which do not use 'useLaunchpadForm' hook.
//
// It's purpose is to alert the user if there are any unsaved changes on the form when
//    1. User refreshes the page
//    2. User performs an action to change location of page (back, submit)
//
// This hook achieves the above two by adding callback handlers on 'beforeunload' window event &
// adding a callback to history.block (called when there is location change)
//
// Typical usage const { formBlock, setBlockingStatus } = useFormBlock({isDirty, message})
//    1. isDirty: This boolean will initially be set to false. A value of true indicates there are unsaved changes on the form. False means original form values are intact.
//    2. message: This is the message that is displayed to the user when navigating away from the current page
//
// Since the hook does not capture form state to know when form is dirty, it exposes a ref 'setBlockingStatus'
// setBlockingStatus(true) will be called consumer of hook to let it know there are unsaved changes on the form
// Prior to submitting the form, 'setBlockingStatus' must be set to 'false' because we don't want to alert the user during form submit

export function useFormBlock({ isDirty, message, isSubmitting }: FormBlock) {
  const history = useHistory();
  const formBlock = useRef<FormBlock>({
    isDirty,
    isSubmitting,
    message,
  });

  const setBlockingStatus = (val: boolean) => {
    formBlock.current.isDirty = val;
  };

  const updateBlock = (data: Partial<FormBlock>) => {
    formBlock.current.isDirty = data.isDirty ?? formBlock.current.isDirty;
    formBlock.current.isSubmitting =
      data.isSubmitting ?? formBlock.current.isSubmitting;
    formBlock.current.message = data.message ?? formBlock.current.message;
  };

  const isBlocking = () => {
    // isSubmitting: do not block
    if (formBlock.current.isSubmitting) return false;
    return formBlock.current.isDirty;
  };

  useEffect(() => {
    const blockHistory = history.block(() => {
      if (isBlocking()) return formBlock.current.message;
      return undefined;
    });
    const beforeUnloadCallback = (e: BeforeUnloadEvent) => {
      if (isBlocking()) {
        e.preventDefault();
        // eslint-disable-next-line no-param-reassign
        e.returnValue = formBlock.current.message;
      }
      return e;
    };
    window.addEventListener('beforeunload', beforeUnloadCallback);
    return () => {
      blockHistory();
      window.removeEventListener('beforeunload', beforeUnloadCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { formBlock, setBlockingStatus, updateBlock };
}
